import type { Collector } from '@/types/odoo';
import {
  buildCollectorenAggregate,
  type MateriaallijstGroup,
  type CollectorSystemSubtotal,
} from '@/utils/warehouseCollectoren';
import { systemPillClass } from '@/utils/warehouse';
import { WerfCollectorPhotoStrip } from '@/components/warehouse/WerfCollectorPhotoStrip';

interface WerfCollectorenTabProps {
  collectors: Collector[];
  projectName: string;
  loading: boolean;
}

function systemLabel(systeem: string): string {
  if (!systeem) return 'Systeem ?';
  return systeem.charAt(0).toUpperCase() + systeem.slice(1);
}

function SystemSubtotalPills({ systems }: { systems: CollectorSystemSubtotal[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {systems.map((s) => (
        <span
          key={s.systeem || '?'}
          className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${systemPillClass(s.systeem)}`}
        >
          {systemLabel(s.systeem)} · {Math.round(s.m2)} m²
        </span>
      ))}
    </div>
  );
}

function CollectorCard({ collector, projectName }: { collector: Collector; projectName: string }) {
  const systeem = collector.x_studio_collector_systeem || '';
  const blok = (collector.x_studio_blok && collector.x_studio_blok[1]) || 'Hoofdgebouw';
  const verdieping = collector.x_studio_verdieping || '+00';
  const kringCount = collector.kringen?.length ?? null;
  const noSystem = !systeem;

  return (
    <div
      className={`rounded-2xl bg-white shadow-sm overflow-hidden ${
        noSystem ? 'border border-dashed border-gray-200 opacity-90' : 'border border-gray-100'
      }`}
    >
      <div className="px-3.5 py-2.5 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-bold text-td-navy">{collector.name}</p>
            <span
              className={`px-1.5 py-0.5 text-[9px] font-bold rounded uppercase ${
                noSystem ? 'bg-gray-100 text-td-light' : systemPillClass(systeem)
              }`}
            >
              {systemLabel(systeem)}
            </span>
          </div>
          <p className="text-[11px] text-td-muted">
            {verdieping} · {blok}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-extrabold text-td-navy">{Math.round(collector.x_studio_collector_m2 || 0)} m²</p>
          <p className="text-[10px] text-td-light">
            {kringCount !== null ? `${kringCount} kringen` : '— kringen'}
          </p>
        </div>
      </div>
      <WerfCollectorPhotoStrip collector={collector} projectName={projectName} />
    </div>
  );
}

function GroupHeader({ group }: { group: MateriaallijstGroup }) {
  const isLinked = group.materiaallijstId !== null;
  const count = group.collectors.length;
  return (
    <div className="flex items-center justify-between px-1 pt-1">
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold rounded-full ${
          isLinked ? 'bg-td-blue/10 text-td-blue' : 'bg-gray-100 text-td-light font-semibold'
        }`}
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 12h6M9 16h6M9 8h6M5 4h14a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
        </svg>
        {isLinked ? group.materiaallijstName : 'Geen materiaallijst'}
      </span>
      {isLinked ? (
        <span className="text-[11px] font-semibold text-td-muted">
          {count} {count === 1 ? 'collector' : 'collectoren'} · {Math.round(group.totalM2)} m²
        </span>
      ) : (
        <span className="text-[11px] font-semibold text-td-light">nog niet gekoppeld</span>
      )}
    </div>
  );
}

/** Read-only collectoren tab: fase total + per-materiaallijst groups with photo strips. */
export function WerfCollectorenTab({ collectors, projectName, loading }: WerfCollectorenTabProps) {
  if (loading) {
    return (
      <div className="py-12 text-center" role="status">
        <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin">
          <span className="sr-only">Laden...</span>
        </div>
      </div>
    );
  }

  if (collectors.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-td-muted text-sm">Geen collectoren gevonden</p>
      </div>
    );
  }

  const agg = buildCollectorenAggregate(collectors);

  return (
    <div className="space-y-3">
      {/* Fase total — materiaallijst-count only shown when collectoren are linked */}
      <div className="rounded-xl bg-td-navy/5 px-3.5 py-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-td-muted">
            {agg.totalCollectors} {agg.totalCollectors === 1 ? 'collector' : 'collectoren'}
            {agg.linkedListCount > 0 && (
              <> · {agg.linkedListCount} {agg.linkedListCount === 1 ? 'materiaallijst' : 'materiaallijsten'}</>
            )}
          </span>
          <span className="text-base font-extrabold text-td-navy">Σ {Math.round(agg.totalM2)} m²</span>
        </div>
        {agg.systems.length > 0 && (
          <div className="mt-2">
            <SystemSubtotalPills systems={agg.systems} />
          </div>
        )}
      </div>

      {agg.linkedListCount === 0 ? (
        /* No collectoren linked to a materiaallijst → just show ALL collectoren
           as a flat list (no "Geen materiaallijst" group header). */
        <div className="space-y-2">
          {agg.groups.flatMap((g) => g.collectors).map((c) => (
            <CollectorCard key={c.id} collector={c} projectName={projectName} />
          ))}
        </div>
      ) : (
        agg.groups.map((group) => (
          <div key={group.materiaallijstId ?? 'none'} className="space-y-2">
            <GroupHeader group={group} />
            {group.collectors.map((c) => (
              <CollectorCard key={c.id} collector={c} projectName={projectName} />
            ))}
          </div>
        ))
      )}
    </div>
  );
}
